/**
 * Day-column grey wash for days blocked by an all-day BUSY event.
 *
 * Once the full-height block became a header chip, a day someone is out for
 * reads as completely open and the chip is easy to miss (ENG-3567 review).
 * This builds one full-day `display: background` event per blocked date so the
 * column is visibly greyed, with the event titles available as a tooltip.
 *
 * Free chips never wash. A recurring "Working from home" is Free and would
 * otherwise grey the entire work week — restating in pixels the exact
 * "treated as a conflict" complaint this ticket exists to fix. Their dashed
 * chip already marks the day without claiming it is unavailable.
 *
 * Grey means "you cannot book here" — NOT "somebody is out". A day the
 * calendar still offers stays clear however many interviewers have an all-day
 * event, via the caller's `isDayBookable` (ENG-3567 review). Each surface
 * answers that from its own model rather than tallying busy interviewers here:
 * the union/intersection rule already differs by interview kind (one_on_one
 * offers a slot when ANY interviewer is free; panel needs EVERY one), so a
 * tally would have to re-derive — and drift from — the availability the
 * pipeline has already computed.
 *
 * Visual only either way: busy all-day events already subtract from
 * availability upstream in `useBusyCalendarEvents`, so the wash adds no
 * booking semantics of its own.
 *
 * Lives beside `AllDayEventChip` because both InterviewManager calendar trees
 * consume it and `ScheduleAll` may not import from `ScheduleInterview`.
 */
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import type { AllDayChipEvent } from './AllDayEventChip';
import { ALL_DAY_EVENT_FALLBACK_TITLE } from './allDayChipCopy';

/** Applied to the background event root; styled in `allDayBlockedOverlay.css`. */
export const ALL_DAY_BLOCKED_CLASS = 'im-all-day-blocked';

/** `extendedProps.category` — how each calendar's `eventContent` finds these. */
export const ALL_DAY_BLOCKED_CATEGORY = 'all-day-blocked';

/**
 * The wash carries its own day's titles in `data-all-day-titles`, so the
 * cursor tooltip reads them straight off whatever it is hovering instead of
 * having a chips map threaded down to it.
 *
 * Unit separator: invisible, never appears in a calendar title, and survives
 * an attribute round-trip unchanged (a newline would not).
 */
const TITLE_SEPARATOR = '\u001f';

export function encodeAllDayTitles(titles: string[]): string {
  return titles.join(TITLE_SEPARATOR);
}

export function decodeAllDayTitles(encoded: string): string[] {
  return encoded.split(TITLE_SEPARATOR).filter(Boolean);
}

/**
 * Titles for the wash under the cursor, given the hit-test stack topmost-first
 * (`document.elementsFromPoint`). `null` when the cursor is not over a wash.
 *
 * A foreground event painted above the wash wins: it has its own tooltip, and
 * stacking a second one on top of it is the opposite of subtle. This is why
 * the caller uses `elementsFromPoint` rather than per-node hover listeners —
 * it needs to see what is layered above the wash, not just the wash.
 */
export function pickAllDayTitlesFromStack(
  stack: readonly Element[]
): string[] | null {
  for (const el of stack) {
    if (!(el instanceof HTMLElement)) continue;
    const encoded = el.dataset.allDayTitles;
    if (encoded) return decodeAllDayTitles(encoded);
    if (
      el.classList.contains('fc-event') &&
      !el.classList.contains('fc-bg-event')
    ) {
      return null;
    }
  }
  return null;
}

export interface BuildAllDayBlockedOverlayEventsArgs {
  /** The same `YYYY-MM-DD` → chips map the day headers render. */
  chipsByDateKey: Record<string, AllDayChipEvent[]>;
  timezone: string;
  /**
   * `YYYY-MM-DD` → the calendar still offers this day. A bookable day is
   * never washed, so the grey can't contradict the slots painted on top of
   * it. Omit on a surface with no availability model — every blocked day
   * washes, which is the pre-gate behaviour.
   */
  isDayBookable?: (_dateKey: string) => boolean;
}

/** The chips that actually block a day — free ones are context, not conflicts. */
export function blockingAllDayChips(
  chips: AllDayChipEvent[] = []
): AllDayChipEvent[] {
  return chips.filter((chip) => !chip.isFree);
}

/**
 * What the hover shows for one day: the calendar's own words for each event,
 * never a generic label (product, ENG-3567 review).
 *
 * `tooltipTitle` wins where the two differ — it is the real event subject,
 * while `title` is the friendlier label the chip shows (a meeting room shows
 * the room name on the chip and the actual meeting on hover). Same precedence
 * `BusyEventContent` uses for grid blocks.
 */
export function allDayBlockedTitles(chips: AllDayChipEvent[]): string[] {
  return chips.map(
    (chip) =>
      chip.tooltipTitle?.trim() ||
      chip.title?.trim() ||
      ALL_DAY_EVENT_FALLBACK_TITLE
  );
}

export function buildAllDayBlockedOverlayEvents({
  chipsByDateKey,
  timezone,
  isDayBookable,
}: BuildAllDayBlockedOverlayEventsArgs): EventInput[] {
  const tz = timezone || moment.tz.guess();
  const events: EventInput[] = [];

  for (const [dateKey, chips] of Object.entries(chipsByDateKey)) {
    // A day whose only all-day events are free stays clear.
    const blocking = blockingAllDayChips(chips);
    if (!blocking.length) continue;
    // Someone is out, but the calendar still has room for this interview.
    if (isDayBookable?.(dateKey)) continue;
    const start = moment.tz(dateKey, 'YYYY-MM-DD', tz).startOf('day');
    if (!start.isValid()) continue;
    // `add(1, 'day')` rather than +24h so a DST day still ends at midnight.
    const end = start.clone().add(1, 'day');
    events.push({
      id: `all-day-blocked-${dateKey}`,
      start: start.toISOString(),
      end: end.toISOString(),
      display: 'background' as const,
      classNames: ALL_DAY_BLOCKED_CLASS,
      color: 'transparent',
      extendedProps: {
        category: ALL_DAY_BLOCKED_CATEGORY,
        // Only the blocking ones — the tooltip explains the grey, and a free
        // chip sitting beside them did not cause it.
        allDayTitles: allDayBlockedTitles(blocking),
      },
    });
  }

  return events;
}
